// ===================================================================
// KAIXO - Error Boundary global
// ===================================================================
// Captura errores de render en cualquier hijo del árbol React y muestra
// una UI amigable en lugar de pantalla en blanco. Envía el error a la
// consola (y opcionalmente al backend) para que quede registrado.
// ===================================================================

import React from 'react';
import { captureError } from '../../utils/sentry';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error('[ErrorBoundary]', error, info);
        // Enviar a Sentry con contexto del árbol React donde falló
        captureError(error, { componentStack: info?.componentStack });
        // Reportar también al backend (best-effort, no bloquea)
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com';
            fetch(`${apiUrl}/api/errors/client`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: error?.message,
                    stack: error?.stack,
                    componentStack: info?.componentStack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    ts: Date.now()
                })
            }).catch(() => {});
        } catch {}
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: 24,
                    background: '#F7F7F7',
                    textAlign: 'center',
                    fontFamily: 'system-ui, sans-serif'
                }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>💔</div>
                    <h2 style={{ color: '#F5A000', marginBottom: 8 }}>Algo ha fallado</h2>
                    <p style={{ color: '#666', maxWidth: 360, marginBottom: 20 }}>
                        Se ha producido un error inesperado. Tu información está a salvo.
                        Toca "Reintentar" para volver a cargar la app.
                    </p>
                    <button
                        onClick={this.handleReload}
                        style={{
                            background: '#F5A000',
                            color: '#fff',
                            border: 'none',
                            padding: '12px 28px',
                            borderRadius: 24,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Reintentar
                    </button>
                    {process.env.NODE_ENV === 'development' && (
                        <pre style={{
                            marginTop: 24,
                            maxWidth: 600,
                            fontSize: 11,
                            color: '#B00',
                            background: '#FFF',
                            padding: 12,
                            borderRadius: 8,
                            overflow: 'auto',
                            textAlign: 'left'
                        }}>
                            {this.state.error?.stack || String(this.state.error)}
                        </pre>
                    )}
                </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
